import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n/LanguageContext";

const VALUES = [120, 48, 24, 9];

function Counter({ target, suffix }) {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const start = performance.now();
          const tick = (now) => {
            const tt = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - tt, 3);
            setN(Math.round(target * eased));
            if (tt < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

export default function Stats() {
  const { t } = useT();
  const items = t("stats.items");

  return (
    <section data-testid="stats-section" className="relative py-24 md:py-36 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl relative">
        <div className="relative rounded-[2.5rem] overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}>
          <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, #FF00E5 0%, transparent 70%)" }} />
          <div className="absolute -bottom-20 -left-16 h-96 w-96 rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 70%)" }} />
          <div className="absolute inset-0 ambient-grid opacity-20" />

          <div className="relative px-8 md:px-16 py-16 md:py-20">
            <div className="flex items-center gap-3 mb-8 reveal">
              <span className="h-[1px] w-10 bg-white/30" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-white/70 font-semibold">
                {t("stats.eyebrow")}
              </span>
            </div>
            <h2 className="reveal font-display text-4xl md:text-6xl font-extralight leading-[1] tracking-[-0.04em] mb-14 max-w-3xl">
              {t("stats.titleA")}
              <br />
              <span className="italic grad-text">{t("stats.titleB")}</span> {t("stats.titleC")}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8">
              {items.map((s, i) => (
                <div key={s.label} className="reveal border-t border-white/15 pt-6" style={{ transitionDelay: `${i * 100}ms` }}>
                  <div className="font-display text-5xl md:text-7xl font-extralight tracking-tight leading-none">
                    <Counter target={VALUES[i]} suffix={s.suffix} />
                  </div>
                  <div className="mt-4 text-[12px] uppercase tracking-[0.2em] text-white/60">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
